import { useCallback, useMemo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ApiKey, Prompt } from '../Types';
import useGlobals from './useGlobals';
import { useParams } from 'react-router-dom';
import { apiKeyService } from '../Services/apiKeyService';
import { SaveApiKey } from '../Types/ServiceTypes';
import { useSelector } from 'react-redux';
import { RootState } from '../Store/Reducers';
import { promptService } from '../Services/promptService';

const useApiKey = () => {
    const { handleNavigate, userSettings, i18n, demo } = useGlobals();
    let { apiKeyIdentifier } = useParams();
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [name, setName] = useState<string>('');
    const [value, setValue] = useState<string>('');
    const [maxCosts, setMaxCosts] = useState<number>(0);
    const [selectedPromptIdentifiers, setSelectedPromptIdentifiers] = useState<string[]>([]);
    const [apiKeyLoaded, setApiKeyLoaded] = useState<boolean>(false);
    const [apiKeySnapshot, setApiKeySnapshot] = useState<ApiKey | null>(null);
    const basePrompt = useSelector((state: RootState) => state.environment.basePrompt!);

    const handleMaxCostChange = useCallback((value: number) => {
        setMaxCosts(value);
    }, []);

    const handleNameChange = useCallback((value: string) => {
        setName(value);
    }, []);

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'apiKeySaved', sourceName: 'ApiKey' }));
    }, [i18n]);

    const handlePromptCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.name === basePrompt.identifier) return;
            const newSelectedPrompts = event.target.checked
                ? [...selectedPromptIdentifiers, event.target.name]
                : selectedPromptIdentifiers.filter((model) => model !== event.target.name);

            setSelectedPromptIdentifiers(newSelectedPrompts);
        },
        [selectedPromptIdentifiers, basePrompt],
    );

    const handleSave = useCallback(
        (name: string, maxCosts: number, apiKeyIdentifier: string) => {
            if (demo) {
                notifySuccess();
                return;
            }

            if (!userSettings.isLoggedIn) return;

            const data = new URLSearchParams() as SaveApiKey;
            data.append('name', name);
            data.append('maxCosts', maxCosts.toString());
            selectedPromptIdentifiers.forEach((prompt: string) => {
                data.append('prompts[]', prompt);
            });

            let type = 'update/' + apiKeyIdentifier;
            if (apiKeyIdentifier === 'new') {
                type = 'create';
            }
            apiKeyService.saveApiKey(type, data).then((response) => {
                notifySuccess();
                apiKeyIdentifier === 'new' && handleNavigate('/admin/apikeys/' + response.data.identifier);
                setApiKeySnapshot(response.data);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        },
        [demo, userSettings.isLoggedIn, selectedPromptIdentifiers, handleNavigate, notifySuccess],
    );

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;

        promptService.getPromptsByPage(1, 10).then((response: { data: { data: Prompt[]; total: number } }) => {
            setSelectedPromptIdentifiers((prevSelectedPrompts) => {
                if (!prevSelectedPrompts.includes(basePrompt.identifier)) {
                    return [...prevSelectedPrompts, basePrompt.identifier];
                }
                return prevSelectedPrompts;
            });

            if (apiKeyIdentifier !== 'new' && apiKeyIdentifier !== undefined) {
                apiKeyService.getApiKey(apiKeyIdentifier).then((response: { data: ApiKey }) => {
                    setName(response.data.name);
                    setValue(response.data.value);
                    setMaxCosts(parseFloat(parseFloat(response.data.maxCosts).toFixed(2)));
                    setSelectedPromptIdentifiers(() => {
                        if (basePrompt.identifier !== '' && !response.data.prompts.includes(basePrompt.identifier)) {
                            return [...response.data.prompts, basePrompt.identifier];
                        }
                        return response.data.prompts;
                    });
                    setApiKeySnapshot(response.data);
                    setApiKeyLoaded(true);
                });
            }
            setPrompts(response.data.data);
            if (apiKeyIdentifier === 'new') {
                setApiKeyLoaded(true);
            }
        });
    }, [userSettings.isLoggedIn, apiKeyIdentifier, demo, basePrompt]);

    const ApiKeyActions = useMemo(
        () => ({
            handleSave,
            handleMaxCostChange,
            handleNameChange,
            handlePromptCheckboxChange,
        }),
        [handleSave, handleMaxCostChange, handleNameChange, handlePromptCheckboxChange],
    );

    return {
        ApiKeyActions,
        apiKeySnapshot,
        prompts,
        name,
        value,
        maxCosts,
        selectedPromptIdentifiers,
        apiKeyLoaded,
        i18n,
        apiKeyIdentifier,
    };
};

export default useApiKey;
